const SEARCH_HISTORY_KEY = 'search_history';

interface SearchHistoryItem {
  keyword: string;
  timestamp: number;
}

/**
 * Get all search history items from localStorage
 */
export const getSearchHistory = (): SearchHistoryItem[] => {
  try {
    const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (!stored) return [];

    const history = JSON.parse(stored) as SearchHistoryItem[];
    // Sort by timestamp (most recent first) and remove duplicates
    const uniqueHistory = Array.from(
      new Map(
        history.map((item) => [item.keyword.toLowerCase(), item])
      ).values()
    );
    return uniqueHistory.sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error('Error reading search history:', error);
    return [];
  }
};

/**
 * Add a keyword to search history
 */
export const addToSearchHistory = (keyword: string): void => {
  if (!keyword || keyword.trim().length < 2) return;

  try {
    const trimmedKeyword = keyword.trim();
    const history = getSearchHistory();

    // Remove existing entry with same keyword (case-insensitive)
    const filteredHistory = history.filter(
      (item) => item.keyword.toLowerCase() !== trimmedKeyword.toLowerCase()
    );

    // Add new entry at the beginning
    const newHistory: SearchHistoryItem[] = [
      { keyword: trimmedKeyword, timestamp: Date.now() },
      ...filteredHistory,
    ];

    // Limit to last 10 searches
    const limitedHistory = newHistory.slice(0, 10);

    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(limitedHistory));
  } catch (error) {
    console.error('Error adding to search history:', error);
  }
};

/**
 * Remove a specific keyword from search history
 */
export const removeFromSearchHistory = (keyword: string): void => {
  try {
    const history = getSearchHistory();
    const filteredHistory = history.filter(
      (item) => item.keyword.toLowerCase() !== keyword.toLowerCase()
    );
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(filteredHistory));
  } catch (error) {
    console.error('Error removing from search history:', error);
  }
};

/**
 * Clear all search history
 */
export const clearSearchHistory = (): void => {
  try {
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  } catch (error) {
    console.error('Error clearing search history:', error);
  }
};
